import { useApolloClient, useMutation } from "@apollo/client/react";
import type { FragmentData } from "@gqlb/core";
import { ChevronDown, ShoppingBag, X } from "lucide-react";
import NextImage from "next/image";
import NextLink from "next/link";
import { useRouter } from "next/router";
import type React from "react";
import { memo, useEffect, useRef, useState } from "react";
import { b } from "@/b";
import LOGO from "../img/logo.svg";
import { SignInModal } from "./SignInModal";

export const HEADER_FRAGMENT = b.fragment("Header_Viewer", "Query", (b) => [
  b.viewer((b) => [
    b.id(),
    b.account((b) => [b.id(), b.featureFlags()]),
    b.employee((b) => [b.id()]),
    b.cart((b) => [
      b.totalQuantity(),
      b.items((b) => [b.__on("CartItemGiftCard", (b) => [b.id()])]),
    ]),
  ]),
]);

const Header_LogoutMutation = b.mutation("Header_Logout", (b) => [
  b.viewer((b) => [b.logout((b) => [b.id(), b.account((b) => [b.id()])])]),
]);

const NavLink: React.FC<{
  href: string;
  children: React.ReactNode;
  vip?: boolean;
  mobile?: boolean;
  onClick?: () => void;
}> = ({ href, children, vip, mobile, onClick }) => {
  const router = useRouter();
  const isActive = router.asPath === href || router.asPath.startsWith(`${href}/`);

  if (mobile) {
    return (
      <NextLink
        href={href}
        onClick={onClick}
        className={`py-3.5 text-lg font-medium border-b border-white/10 transition-colors duration-200 ${
          vip ? "text-white font-semibold" : isActive ? "text-brand-mint" : "text-white/70"
        }`}
      >
        {vip ? (
          <span className="bg-brand-mint text-brand-black px-3 py-1 rounded-full">{children}</span>
        ) : (
          children
        )}
      </NextLink>
    );
  }

  if (vip) {
    return (
      <NextLink
        href={href}
        className={`px-4 py-1.5 rounded-full font-semibold text-sm transition-all duration-200 ${
          isActive
            ? "bg-brand-mint text-brand-black shadow-sm shadow-brand-mint/40"
            : "bg-brand-mint/80 text-brand-black hover:bg-brand-mint hover:shadow-sm hover:shadow-brand-mint/40"
        }`}
      >
        {children}
      </NextLink>
    );
  }

  return (
    <NextLink
      href={href}
      className={`relative text-sm font-medium tracking-wide transition-colors duration-200 py-1 ${
        isActive
          ? "text-brand-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-gray-900 after:rounded-full"
          : "text-gray-500 hover:text-brand-black"
      }`}
    >
      {children}
    </NextLink>
  );
};

const DropdownMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isTouchDevice = useRef(false);

  useEffect(() => {
    const handlePointerDown = () => {
      isTouchDevice.current = true;
    };
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  return (
    <nav
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onClickCapture={(e) => {
        if (isOpen) return;
        if (isTouchDevice.current && !isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }
      }}
    >
      <div className="flex items-center gap-1 cursor-pointer">
        <NavLink href="/our-story">Our Story</NavLink>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-48 bg-white rounded-xl shadow-lg shadow-black/8 border border-gray-100 py-2 transition-all duration-200 ${
          isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
        }`}
      >
        <NextLink
          href="/meet-the-team"
          className="block px-4 py-2.5 text-sm text-gray-600 hover:text-brand-black hover:bg-brand-mint/10 transition-colors rounded-lg mx-1"
        >
          Meet the Team
        </NextLink>
        <NextLink
          href="/our-partners"
          className="block px-4 py-2.5 text-sm text-gray-600 hover:text-brand-black hover:bg-brand-mint/10 transition-colors rounded-lg mx-1"
        >
          Our Partners
        </NextLink>
      </div>
    </nav>
  );
};

export const Header: React.FC<{
  data: FragmentData<typeof HEADER_FRAGMENT>;
  banner?: React.ReactNode;
  overlay?: boolean;
}> = memo(({ data, banner, overlay = false }) => {
  const _router = useRouter();
  const headerRef = useRef<HTMLElement>(null);
  const isEmployee = data.viewer.employee !== null;
  // totalQuantity counts meals (it backs the meal minimum); gift cards add one each.
  const totalQuantity =
    data.viewer.cart.totalQuantity +
    data.viewer.cart.items.filter((i) => i.__typename === "CartItemGiftCard").length;
  const account = data.viewer.account;

  const [signInOpen, setSignInOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutMutation] = useMutation(Header_LogoutMutation.document());
  const _client = useApolloClient();

  const handleLogout = async () => {
    await logoutMutation();
    window.location.reload();
  };

  const closeMenu = () => setMenuOpen(false);

  // Expose the actual rendered header height (including banner) as a CSS variable.
  // This lets sticky elements (e.g. the menu category bar) align correctly even
  // when the banner wraps to multiple lines on small screens.
  // biome-ignore lint/correctness/useExhaustiveDependencies: banner/overlay deps trigger re-measurement when visibility changes
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const setVar = () => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty("--vm-header-height", `${h}px`);
    };

    setVar();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => setVar());
      ro.observe(el);
    } else {
      window.addEventListener("resize", setVar);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", setVar);
    };
  }, [banner, overlay]);

  return (
    <>
      <header ref={headerRef} className={`${overlay ? "fixed" : "sticky"} top-0 z-50 w-full`}>
        {banner && (
          <div className="bg-brand-mint overflow-hidden">
            <p className="text-gray-700 text-xs lg:text-sm text-center py-2.5 lg:py-3 px-8 md:px-16 font-medium">
              {banner}
            </p>
          </div>
        )}
        <div className="bg-white/90 backdrop-blur-lg border-b border-gray-100/80">
          <div className="max-w-[1920px] mx-auto h-14 lg:h-16 px-4 lg:px-8 flex items-center">
            <SignInModal open={signInOpen} setOpen={setSignInOpen} />

            {/* Logo */}
            <NextLink href="/" className="flex items-center gap-2 md:gap-3 shrink-0">
              <NextImage
                src={LOGO}
                alt="Visionary Meals"
                className="w-8 h-8 md:w-10 md:h-10 xl:w-12 xl:h-12"
                priority
              />
              <span className="uppercase text-brand-black text-xl xl:text-2xl font-light tracking-widest hidden lg:inline">
                Visionary Meals
              </span>
            </NextLink>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-5 lg:gap-6 ml-auto">
              <NavLink href="/menu">Menu</NavLink>
              <NavLink href="/nutrition">Nutrition</NavLink>
              <NavLink href="/vip" vip>
                VIP
              </NavLink>
              <NavLink href="/swag">Swag</NavLink>
              <NavLink href="/gift-cards">Gift Cards</NavLink>
              <DropdownMenu />
              <NavLink href="/faq">FAQ</NavLink>

              {/* Cart */}
              <NextLink
                href="/cart"
                className="relative flex items-center text-gray-500 hover:text-brand-black transition-colors duration-200"
              >
                <ShoppingBag className="w-5 h-5" />
                {totalQuantity > 0 && (
                  <span className="absolute -top-2 -right-2.5 bg-brand-mint text-brand-black text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                    {totalQuantity}
                  </span>
                )}
              </NextLink>

              {/* Auth */}
              {account === null ? (
                <button
                  type="button"
                  onClick={() => setSignInOpen(true)}
                  className="text-sm text-gray-500 hover:text-brand-black transition-colors duration-200 font-medium cursor-pointer"
                >
                  Sign In
                </button>
              ) : (
                <NavLink href="/account">Account</NavLink>
              )}
              {isEmployee && <NavLink href="/admin">Admin</NavLink>}
              {account !== null && (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-sm text-gray-400 hover:text-brand-black transition-colors duration-200 cursor-pointer"
                >
                  Logout
                </button>
              )}
            </nav>

            {/* Mobile: Cart + Hamburger */}
            <div className="flex items-center gap-4 ml-auto md:hidden">
              <NextLink href="/cart" className="relative flex items-center text-gray-500">
                <ShoppingBag className="w-5 h-5" />
                {totalQuantity > 0 && (
                  <span className="absolute -top-2 -right-2.5 bg-brand-mint text-brand-black text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                    {totalQuantity}
                  </span>
                )}
              </NextLink>

              <button
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex flex-col items-center justify-center w-10 h-10 gap-1.5 cursor-pointer"
                aria-label="Toggle menu"
              >
                <span
                  className={`block w-6 h-0.5 bg-gray-600 transition-all duration-300 origin-center ${
                    menuOpen ? "rotate-45 translate-y-2" : ""
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 bg-gray-600 transition-all duration-300 ${
                    menuOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 bg-gray-600 transition-all duration-300 origin-center ${
                    menuOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Backdrop */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss pattern */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
          menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMenu}
        onKeyDown={() => {}}
        role="presentation"
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[85%] max-w-sm bg-brand-black z-50 flex flex-col transition-transform duration-300 ease-out md:hidden ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-end p-5">
          <button
            type="button"
            onClick={closeMenu}
            className="w-8 h-8 flex items-center justify-center cursor-pointer text-white/60 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex flex-col px-6 flex-1 overflow-y-auto">
          <NavLink href="/menu" mobile onClick={closeMenu}>
            Menu
          </NavLink>
          <NavLink href="/nutrition" mobile onClick={closeMenu}>
            Nutrition
          </NavLink>
          <NavLink href="/vip" vip mobile onClick={closeMenu}>
            VIP
          </NavLink>
          <NavLink href="/swag" mobile onClick={closeMenu}>
            Swag
          </NavLink>
          <NavLink href="/gift-cards" mobile onClick={closeMenu}>
            Gift Cards
          </NavLink>
          <NavLink href="/our-story" mobile onClick={closeMenu}>
            Our Story
          </NavLink>
          <NavLink href="/meet-the-team" mobile onClick={closeMenu}>
            Meet the Team
          </NavLink>
          <NavLink href="/our-partners" mobile onClick={closeMenu}>
            Our Partners
          </NavLink>
          <NavLink href="/faq" mobile onClick={closeMenu}>
            FAQ
          </NavLink>
          <NextLink
            href="/cart"
            onClick={closeMenu}
            className="flex items-center gap-3 py-3.5 text-lg font-medium text-white border-b border-white/10"
          >
            <span className="relative mr-1">
              <ShoppingBag className="size-5" />
              {totalQuantity > 0 && (
                <span className="absolute -top-2 -right-2.5 bg-brand-mint text-brand-black text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">
                  {totalQuantity}
                </span>
              )}
            </span>
            Cart
          </NextLink>
          <div className="pt-4 flex flex-col gap-3">
            {account === null ? (
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  setSignInOpen(true);
                }}
                className="text-lg text-white/50 font-medium text-left cursor-pointer hover:text-white transition-colors"
              >
                Sign In
              </button>
            ) : (
              <NavLink href="/account" mobile onClick={closeMenu}>
                Account
              </NavLink>
            )}
            {isEmployee && (
              <NavLink href="/admin" mobile onClick={closeMenu}>
                Admin
              </NavLink>
            )}
            {account !== null && (
              <button
                type="button"
                onClick={() => {
                  closeMenu();
                  handleLogout();
                }}
                className="text-lg text-white/40 font-medium text-left cursor-pointer hover:text-white transition-colors"
              >
                Logout
              </button>
            )}
          </div>
        </nav>
      </div>
    </>
  );
});

Header.displayName = "Header";
